import type { SelectOption } from "@ir/ui";
import type { DeductionTypeSelectGroup } from "./MasterDataReasonCodeMappingAddEditForm";

type SelectOptionOrGroup = SelectOption & { options?: SelectOption[] };

/**
 * Builds react-select options for Deduction Type:
 * - Types with subtypes become non-clickable group headers; subtypes are selectable.
 * - Types with no subtypes become selectable leaf options.
 */
export function buildDeductionTypeSelectOptions(
  groups: DeductionTypeSelectGroup[],
  currentOption?: { typeId: number; typeName: string } | null,
): SelectOption[] {
  const options: SelectOptionOrGroup[] = [];
  const selectableIds = new Set<number>();

  for (const group of groups) {
    const subtypes = group.subtypes ?? [];
    if (subtypes.length > 0) {
      for (const subtype of subtypes) {
        selectableIds.add(subtype.typeId);
      }
      options.push({
        label: group.typeName,
        value: String(group.typeId),
        options: subtypes.map((subtype) => ({
          label: subtype.typeName,
          value: String(subtype.typeId),
        })),
      });
      continue;
    }

    selectableIds.add(group.typeId);
    options.push({
      label: group.typeName,
      value: String(group.typeId),
    });
  }

  if (currentOption && !selectableIds.has(currentOption.typeId)) {
    options.unshift({
      label: currentOption.typeName,
      value: String(currentOption.typeId),
    });
  }

  return options as SelectOption[];
}

/** Resolve a selected value to a leaf option only (never a group header). */
export function findLeafOptionByValue(
  options: SelectOption[],
  value: number | null,
): SelectOption | undefined {
  const raw = value == null ? null : String(value);
  if (raw == null) return undefined;

  for (const option of options as SelectOptionOrGroup[]) {
    if (option.options) {
      const match = option.options.find((child) => child.value === raw);
      if (match) return match;
      continue;
    }
    if (option.value === raw) return option;
  }
  return undefined;
}

export function formatDeductionTypeGroupLabel(group: {
  label?: string;
}): string {
  return group.label ?? "";
}

export const DEDUCTION_TYPE_SELECT_CLASS = "deduction-type-grouped-select";
